import { Radio, Switch, TextLink, Tooltip, Typography } from '@neo4j-ndl/react';
import {
  CheckCircleIconSolid,
  ExclamationTriangleIconOutline,
  InformationCircleIconOutline,
} from '@neo4j-ndl/react/icons';
import type { Organization } from '@nx/state';
import { TIER } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import type { JSX } from 'react';

import { MINIMUM_GDS_PLUGIN_VECTOR_MEMORY } from '../constants';
import { gbStringToInt } from '../entities/helpers';
import type { InstanceFormData } from '../entities/model';

type InstanceGdsPluginFormData = Pick<InstanceFormData, 'size' | 'version' | 'gdsPlugin' | 'tier'>;

const gdsPluginDisabledReason = (data: InstanceGdsPluginFormData) => {
  if (isNullish(data.size)) {
    return 'You must select a size before enabling this feature';
  }

  if (![TIER.PROFESSIONAL].includes(data.tier)) {
    return 'GDS plugin is not available for this tier';
  }

  if (data.version !== '5') {
    return 'GDS plugin is not available for the selected version';
  }

  if (gbStringToInt(data.size.memory) < MINIMUM_GDS_PLUGIN_VECTOR_MEMORY) {
    return '4GB memory or greater is required for the GDS plugin configuration';
  }

  return null;
};

export const getValidGdsPluginValue = (data: InstanceGdsPluginFormData) => {
  if (!gdsPluginDisabledReason(data)) {
    return data.gdsPlugin;
  }

  if ([TIER.GDS, TIER.AURA_DSE].includes(data.tier)) {
    return true;
  }

  return false;
};

const GdsAnalyticsPluginDisableReason = (data: InstanceGdsPluginFormData) => {
  if ([TIER.ENTERPRISE].includes(data.tier)) {
    return 'Graph Analytics Plugin is unavailable on Virtual Dedicated Cloud';
  }
  if ([TIER.MTE].includes(data.tier)) {
    return 'Graph Analytics Plugin is unavailable on Business Critical';
  }
  return gdsPluginDisabledReason(data);
};

type GdsAnalyticsCreateProps = {
  data: InstanceGdsPluginFormData;
  organization: Organization;
  onChange: (gdsPlugin: boolean) => void;
};

type MessageText = {
  text: string | JSX.Element;
};

const TriangleWarningText = ({ text }: MessageText) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <ExclamationTriangleIconOutline strokeWidth="2" className="stroke-palette-warning-icon size-5" />
      <Typography variant="body-medium" className="text-warning-text">
        {text}
      </Typography>
    </div>
  );
};

const CircleSuccessText = ({ text }: MessageText) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <CheckCircleIconSolid className="text-success-icon size-5" />
      <Typography variant="body-medium" className="text-success-text">
        {text}
      </Typography>
    </div>
  );
};

const CircleInfoText = ({ text }: MessageText) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <InformationCircleIconOutline className="text-info-icon size-5" />
      <Typography variant="body-medium" className="text-info-text">
        {text}
      </Typography>
    </div>
  );
};

type ServerlessDisabledWarningInput = {
  organization: Organization;
};

const ServerlessDisabledWarning = ({ organization }: ServerlessDisabledWarningInput) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <ExclamationTriangleIconOutline strokeWidth="2" className="stroke-palette-warning-icon size-5" />
      <Typography variant="body-medium" className="text-warning-text">
        <b>Graph Analytics Serverless</b> has been disabled by an organization admin. Go to{' '}
        <TextLink className="no-underline" href={`/org/${organization.id}/settings`}>
          Settings
        </TextLink>{' '}
        to enable the feature.
      </Typography>
    </div>
  );
};

const GraphAnalyticsServerlessField = ({ data, organization, onChange }: GdsAnalyticsCreateProps) => {
  const handleGdsPluginChange = (value: boolean) => {
    // true = plugin enabled / serverless disabled
    // false = plugin disabled / serverless enabled
    onChange(value);
  };

  const serverlessEnabled = organization.gdsSessions.enabled;
  const isProfessionalTier = data.tier === TIER.PROFESSIONAL;

  let optionDetails;
  // Helper to render the recommended/success or warning/info message
  if (serverlessEnabled) {
    if (data.gdsPlugin) {
      optionDetails = (
        <TriangleWarningText
          text={
            <>
              <b>Graph Analytics Serverless</b> <text> will not be available if the plugin is enabled.</text>
            </>
          }
        />
      );
    } else {
      optionDetails = (
        <CircleSuccessText text={'Recommended for dynamic scaling and minimal infrastructure overhead.'} />
      );
    }
  } else {
    optionDetails = <ServerlessDisabledWarning organization={organization} />;
  }

  return (
    <div className="flex flex-row items-center justify-between py-2">
      <div className="flex flex-col gap-2">
        <Typography variant="subheading-medium">
          {serverlessEnabled ? 'Serverless (recommended)' : 'Serverless (unavailable)'}
        </Typography>
        <Typography variant="body-medium" as="div" className="text-neutral-text-weak">
          Configure the instance to run graph algorithms in an on-demand ephemeral compute environment.
        </Typography>
        {optionDetails}
      </div>

      {serverlessEnabled && isProfessionalTier && (
        <Radio
          isChecked={!data.gdsPlugin}
          onChange={() => handleGdsPluginChange(false)}
          ariaLabel="Enable Graph Analytics Serverless"
        />
      )}

      {serverlessEnabled && !isProfessionalTier && (
        <Radio isChecked={true} ariaLabel="Enable Graph Analytics Serverless" />
      )}

      {!serverlessEnabled && !isProfessionalTier && (
        <Tooltip type="simple" isPortaled={false} isDisabled={false}>
          <Tooltip.Trigger>
            <Radio isChecked={false} isDisabled={true} ariaLabel="Enable Graph Analytics Serverless" />
          </Tooltip.Trigger>
          <Tooltip.Content>Graph Analytics Serverless has been disabled by an organization admin.</Tooltip.Content>
        </Tooltip>
      )}
    </div>
  );
};

const GraphAnalyticsPluginField = ({ data, organization, onChange }: GdsAnalyticsCreateProps) => {
  const handleGdsPluginChange = (value: boolean) => {
    // true = plugin enabled / serverless disabled
    // false = plugin disabled / serverless enabled
    onChange(value);
  };

  const serverlessEnabled = organization.gdsSessions.enabled;
  const pluginDisabledReason = GdsAnalyticsPluginDisableReason(data);
  const isDisabled = isNotNullish(pluginDisabledReason);
  const pluginToggleLabel = 'Toggle to install GDS plugin on instance';

  const isProfessionalTier = data.tier === TIER.PROFESSIONAL;

  let optionDetails;
  // Helper to render the unavailable/info message for different tiers
  if (!isProfessionalTier) {
    optionDetails = <CircleInfoText text={'Unavailable for this tier.'} />;
  }

  return (
    <div className="flex flex-row items-center justify-between py-2">
      <div className="flex flex-col gap-2">
        <Typography variant="subheading-medium">Plugin {isProfessionalTier ? '' : ' (unavailable)'}</Typography>
        <Typography variant="body-medium" as="div" className="text-neutral-text-weak">
          Enable the instance to run graph algorithms sharing memory and compute with the database.
        </Typography>
        {optionDetails}
      </div>

      {!isProfessionalTier && (
        <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
          <Tooltip.Trigger>
            <Radio
              isChecked={data.gdsPlugin}
              onChange={() => handleGdsPluginChange(true)}
              isDisabled={isDisabled}
              ariaLabel="Enable Graph Analytics Plugin"
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{pluginDisabledReason}</Tooltip.Content>
        </Tooltip>
      )}

      {isProfessionalTier && serverlessEnabled && (
        <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
          <Tooltip.Trigger>
            <Radio
              isChecked={data.gdsPlugin}
              onChange={() => handleGdsPluginChange(true)}
              isDisabled={isDisabled}
              ariaLabel="Enable Graph Analytics Plugin"
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{pluginDisabledReason}</Tooltip.Content>
        </Tooltip>
      )}

      {isProfessionalTier && !serverlessEnabled && (
        <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
          <Tooltip.Trigger>
            <Switch
              htmlAttributes={{
                'aria-label': pluginToggleLabel,
                'data-testid': 'gds-plugin-switch',
              }}
              ariaLabel={pluginToggleLabel}
              isChecked={data.gdsPlugin}
              onChange={(e) => handleGdsPluginChange(e.target.checked)}
              isDisabled={isDisabled}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{pluginDisabledReason}</Tooltip.Content>
        </Tooltip>
      )}
    </div>
  );
};

export const GraphAnalyticsSection = ({ data, organization, onChange }: GdsAnalyticsCreateProps) => {
  return (
    <>
      <GraphAnalyticsServerlessField organization={organization} data={data} onChange={onChange} />
      <GraphAnalyticsPluginField organization={organization} data={data} onChange={onChange} />
    </>
  );
};

type InstanceVectorOptimizedFormData = Pick<InstanceFormData, 'size' | 'version' | 'vectorOptimized' | 'tier'>;

const vectorOptimizedDisabledReason = (data: InstanceVectorOptimizedFormData) => {
  if (isNullish(data.size)) {
    return 'You must select a size before enabling this feature';
  }

  if (![TIER.PROFESSIONAL, TIER.MTE, TIER.ENTERPRISE].includes(data.tier)) {
    return 'Vector index configuration is not available for this tier';
  }

  if (data.version !== '5') {
    return 'Vector index configuration is not available for the selected version';
  }

  if (gbStringToInt(data.size.memory) < MINIMUM_GDS_PLUGIN_VECTOR_MEMORY) {
    return '4GB memory or greater is required for the vector index configuration';
  }

  return null;
};

export const getValidVectorOptimizedValue = (data: InstanceVectorOptimizedFormData) => {
  if (!vectorOptimizedDisabledReason(data)) {
    return data.vectorOptimized;
  }

  return false;
};

type VectorOptimizedCreateProps = {
  data: InstanceVectorOptimizedFormData;
  onChange: (vectorOptimized: boolean) => void;
};

export const VectorOptimizedField = ({ data, onChange }: VectorOptimizedCreateProps) => {
  const disabledReason = vectorOptimizedDisabledReason(data);
  const isDisabled = isNotNullish(disabledReason);
  const label = 'Toggle on Allocate vector index memory';

  return (
    <div className="flex flex-row items-center justify-between py-2">
      <div className="flex flex-col gap-2">
        <Typography variant="subheading-medium">Vector-optimized configuration</Typography>
        <Typography variant="body-medium" as="div" className="text-neutral-text-weak">
          Optimize Neo4j for GraphRAG, GenAI, and semantic search applications that index and search vector embeddings.
          See{' '}
          <TextLink href="https://neo4j.com/docs/aura/managing-instances/vector-optimization/" type="external">
            documentation
          </TextLink>{' '}
          for details.
        </Typography>
      </div>
      <div>
        <Tooltip type="simple" isPortaled={false} isDisabled={!isDisabled}>
          <Tooltip.Trigger>
            <Switch
              htmlAttributes={{
                'aria-label': label,
                'data-testid': 'vector-optimized-switch',
              }}
              ariaLabel={label}
              isChecked={data.vectorOptimized}
              onChange={(e) => onChange(e.target.checked)}
              isDisabled={isDisabled}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{disabledReason}</Tooltip.Content>
        </Tooltip>
      </div>
    </div>
  );
};

export const MigrateToBlockField = ({
  onChange,
  migrateToBlock,
}: {
  onChange: (migrateToBlock: boolean) => void;
  migrateToBlock: boolean;
}) => {
  const label = 'Migrate database store format to block storage (recommended)';

  return (
    <div className="flex flex-row items-center justify-between py-2">
      <div className="flex flex-col gap-2">
        <Typography variant="subheading-medium">
          Migrate database store format to block storage (recommended)
        </Typography>
        <Typography variant="body-medium" as="div" className="text-neutral-text-weak">
          Migrate the database store format to block storage to improve performance and reduce costs. See{' '}
          <TextLink
            type="external"
            href="https://neo4j.com/docs/operations-manual/current/database-internals/store-formats/"
          >
            documentation
          </TextLink>{' '}
          for details.
        </Typography>
      </div>
      <div>
        <Tooltip type="simple" isPortaled={false} isDisabled={false}>
          <Tooltip.Trigger>
            <Switch
              htmlAttributes={{
                'aria-label': label,
                'data-testid': 'migrate-to-block-switch',
              }}
              ariaLabel={label}
              isChecked={migrateToBlock}
              onChange={(e) => onChange(e.target.checked)}
              isDisabled={false}
            />
          </Tooltip.Trigger>
        </Tooltip>
      </div>
    </div>
  );
};
